import Vue from 'vue';

interface EventHandlers {
  [keyof: string]: (...args: any[]) => void;
}

export default function useFunctionCall<T = any, U = EventHandlers>(Component: any) {
  let instance: any = null;

  function isShowing() {
    return instance !== null;
  }

  function close() {
    document.body.style.overflow = 'visible';
    if (instance !== null) {
      document.body.removeChild(instance.$el);
      instance = null;
    }
  }

  /**
   * 显示组件
   * @param {T} propsData - 相当于声明式组件的 props
   * @param {U} on - 相当于声明式组件的 v-on:eventName="" (@eventName="")
   * @param {string} hideTriggerEventName - 如果你的组件是通过 $emit('eventName') 实现组件关闭的 这个入参填其事件名, 且确保 on 中的 key 包括该事件名
   * @param {string} hideTriggerPropName - 如果你的组件是通过传入一个类型为 Function 的 props 实现组件关闭的 这个入参填其属性名, 且确保 propsData 中的 key 包括该属性名
   */
  function show(propsData: T = {} as T, on: U = {} as U, hideTriggerEventName?: string, hideTriggerPropName?: string) {
    if (instance !== null && !(propsData as any).multiInstance) close();

    document.body.style.overflow = 'hidden';
    return new Promise((resolve, reject) => {
      const Ctor = Vue.extend(Component);
      const defaultOptions: any = {
        propsData,
      };

      instance = new Ctor(defaultOptions);
      instance.$mount();
      document.body.appendChild(instance.$el);

      // 如果是通过一个类型为 Function 的 props 实现组件的关闭, 则注入隐藏组件的逻辑
      if (
        hideTriggerPropName &&
        propsData[hideTriggerPropName as keyof typeof propsData] &&
        typeof propsData[hideTriggerPropName as keyof typeof propsData] === 'function'
      ) {
        const originalFunction = propsData[hideTriggerPropName as keyof typeof propsData];
        (propsData[hideTriggerPropName as keyof typeof propsData] as any) = (...args: any[]) => {
          (originalFunction as any)(args);
          close();
        };
      }

      // 默认绑定事件
      const defaultEvents = {
        confirm: (value: string) => {
          resolve(value);
        },
        cancel: (value: any) => {
          reject(value);
        },
        closeSlider: () => {
          close();
        },
      };

      const totalEvents = {
        ...defaultEvents,
        ...on,
      };

      // 绑定 on 中声明的事件
      Object.keys(totalEvents).forEach((eventName) => {
        instance.$on(eventName, (...args: any[]) => {
          if (on[eventName as keyof typeof on] && typeof on[eventName as keyof typeof on] === 'function') {
            (on[eventName as keyof typeof on] as any)(args);
          } else if (typeof totalEvents[eventName as keyof typeof on] === 'function') {
            (totalEvents[eventName as keyof typeof on] as any)(args);
          }
          // 如果是通过事件关闭组件的, 则注入隐藏组件的逻辑
          if (eventName === hideTriggerEventName) {
            close();
          }
        });
      });
    });
  }

  return { show, close, isShowing };
}
